/**
 * Account Manager for centralized authentication and license management via Backend API.
 */
import { featureFlags } from './FeatureFlagService.js';

export class AccountManager {
    constructor() {
        this.currentUser = null;
        this.token = null;
        this.onChanged = null;
        this.API_URL = import.meta.env.VITE_API_URL || '/api';
        this.loadFromStorage();
        this.checkUrlForToken();
    }

    /**
     * Retrieves or generates a persistent anonymous device UUID stored in local storage.
     * @returns {string|null} Unique device UUID string or null if localStorage unavailable.
     */
    getAnonymousId() {
        if (typeof localStorage === 'undefined') return null;
        let anonId = localStorage.getItem('transwasm_anon_id');
        if (!anonId) {
            anonId = (typeof crypto !== 'undefined' && crypto.randomUUID)
                ? crypto.randomUUID()
                : 'anon_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
            localStorage.setItem('transwasm_anon_id', anonId);
        }
        return anonId;
    }

    /**
     * Constructs HTTP request headers including Authorization JWT token and/or X-Anonymous-ID header.
     * @param {Object} [additionalHeaders={}] Optional additional headers object.
     * @returns {Object} Complete headers object for API fetch calls.
     */
    getHeaders(additionalHeaders = {}) {
        const headers = { ...additionalHeaders };
        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }
        const anonId = this.getAnonymousId();
        if (anonId) {
            headers['X-Anonymous-ID'] = anonId;
        }
        return headers;
    }

    /**
     * Loads credentials from local storage (for persistence).
     */
    loadFromStorage() {
        if (typeof localStorage === 'undefined') return;
        const saved = localStorage.getItem('transwasm_user');
        const token = localStorage.getItem('transwasm_token');
        if (saved && token) {
            try {
                this.currentUser = JSON.parse(saved);
                this.token = token;

                // Track session restore
                if (window.mixpanel && this.currentUser) {
                    window.mixpanel.identify(this.currentUser.id || this.currentUser.email);
                }

                // Proactively refresh profile to get latest licenses/tier
                this.refreshProfile();
            } catch (e) {
                // console.error("Failed to parse saved user", e);
                this.logout();
            }
        }
    }

    async checkUrlForToken() {
        if (typeof window === 'undefined') return;
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        const error = urlParams.get('error');
        const purchase = urlParams.get('purchase');

        if (error) {
            alert('Authentication Error: ' + decodeURIComponent(error));
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
        }

        if (purchase === 'success') {
            await this.refreshProfile();
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
            
            // Dispatch custom event for UI to pick up
            window.dispatchEvent(new CustomEvent('purchase-success'));

            // Re-broadcast change to notify UI
            if (this.onChanged && this.currentUser) this.onChanged(this.currentUser);
            return;
        }

        if (token) {
            this.token = token;
            localStorage.setItem('transwasm_token', token);
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);

            try {
                await this.refreshProfile();
                // Double check if we are authenticated now
                if (!this.currentUser) {
                    throw new Error('Profile fetch failed after token set.');
                }
            } catch (err) {
                // console.error('[AccountManager] Token verification failed:', err);
                alert('Authentication failed: Your session may have expired. Please login again.');
                this.logout();
            }
        }
    }

    saveToStorage() {
        if (typeof localStorage === 'undefined') return;
        if (this.currentUser && this.token) {
            localStorage.setItem('transwasm_user', JSON.stringify(this.currentUser));
            localStorage.setItem('transwasm_token', this.token);
        } else {
            localStorage.removeItem('transwasm_user');
            localStorage.removeItem('transwasm_token');
        }
    }

    /**
     * Refreshes user profile data from the server.
     */
    async refreshProfile() {
        if (!this.token || this.token === 'mock-test-token') return;
        try {
            const res = await fetch(`${this.API_URL}/user/profile`, {
                headers: { 'Authorization': `Bearer ${this.token}` }
            });
            if (res.ok) {
                this.currentUser = await res.json();
                this.currentUser.beta_mode = this.currentUser.beta_mode || false; // Ensure boolean
                this.saveToStorage();
                // Refresh feature flags alongside the profile.
                await featureFlags.refresh(this.token);
                if (this.onChanged) this.onChanged(this.currentUser);
            } else if (res.status === 401 || res.status === 403) {
                // console.warn('[AccountManager] Profile refresh unauthorized. Logging out.');
                this.logout();
            }
        } catch (e) {
            // console.error("[AccountManager] Profile refresh failed", e);
        }
    }

    async login(email, password) {
        const res = await fetch(`${this.API_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Login failed');

        this.currentUser = data.user;
        this.token = data.token;

        if (window.mixpanel) {
            window.mixpanel.identify(this.currentUser.id || this.currentUser.email);
            window.mixpanel.people.set({
                "$email": this.currentUser.email,
                "$name": this.currentUser.name
            });
            window.mixpanel.track('Login');
        }

        // Fetch full profile to get licenses
        await this.refreshProfile();

        this.saveToStorage();
        return this.currentUser;
    }

    async signUp(email, password, name) {
        const res = await fetch(`${this.API_URL}/auth/signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, name })
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Sign up failed');

        if (data.waitlist && data.waitlist.pendingVerification) {
            // Do NOT log in. Return data so UI can show message.
            if (window.mixpanel) {
                window.mixpanel.track('Join Waitlist');
            }
            return data;
        }

        this.currentUser = data.user;
        this.token = data.token;

        if (window.mixpanel && this.currentUser) {
            window.mixpanel.identify(this.currentUser.id || this.currentUser.email);
            window.mixpanel.people.set({
                "$email": this.currentUser.email,
                "$name": this.currentUser.name
            });
            window.mixpanel.track('Sign Up');
        }

        // Initial profile (won't have licenses yet but gets the tier)
        await this.refreshProfile();

        this.saveToStorage();
        // Return full data to allow UI to check for waitlist
        return data;
    }

    // --- Pricing & Licensing ---

    async getPricing() {
        const res = await fetch(`${this.API_URL}/pricing`);
        if (res.ok) return await res.json();
        return [];
    }

    async getFreeModePricing() {
        const res = await fetch(`${this.API_URL}/free-mode-pricing`);
        if (res.ok) return await res.json();
        return null;
    }

    async purchaseLicense(tierId) {
        if (!this.token) throw new Error('Not authenticated');
        const res = await fetch(`${this.API_URL}/user/purchase`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.token}`
            },
            body: JSON.stringify({ tierId })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Purchase failed');

        if (window.mixpanel) {
            window.mixpanel.track('Purchase License', {
                tierId: tierId
            });
        }

        // Refresh profile to update credits
        await this.refreshProfile();
        return data;
    }

    async createCheckoutSession(options) {
        if (!this.token) throw new Error('Not authenticated');
        let body = {};
        if (typeof options === 'object' && options !== null) {
            body = options;
        } else {
            body = { tierId: options };
        }
        const res = await fetch(`${this.API_URL}/user/checkout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.token}`
            },
            body: JSON.stringify(body)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to create checkout session');
        return data;
    }

    // --- Admin Methods ---

    async getAdminPricing() {
        if (!this.token) return [];
        const res = await fetch(`${this.API_URL}/admin/pricing`, {
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        if (res.ok) return await res.json();
        return [];
    }

    async saveAdminPricing(tier) {
        if (!this.token) return;
        const res = await fetch(`${this.API_URL}/admin/pricing`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.token}`
            },
            body: JSON.stringify(tier)
        });
        if (!res.ok) throw new Error('Failed to save pricing tier');
    }

    async deleteAdminPricing(id) {
        if (!this.token) return;
        const res = await fetch(`${this.API_URL}/admin/pricing/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        if (!res.ok) throw new Error('Failed to delete pricing tier');
    }

    async getAdminFreeModePricing() {
        if (!this.token) return null;
        const res = await fetch(`${this.API_URL}/admin/free-mode-pricing`, {
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        if (res.ok) return await res.json();
        return null;
    }

    async saveAdminFreeModePricing(pricing) {
        if (!this.token) return;
        const res = await fetch(`${this.API_URL}/admin/free-mode-pricing`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.token}`
            },
            body: JSON.stringify({ pricing })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to save Free Mode pricing');
        return data;
    }

    async verifyEmail(token) {
        const res = await fetch(`${this.API_URL}/auth/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Verification failed');
        return data;
    }

    async resendVerification() {
        if (!this.token) throw new Error('You must be signed in to resend verification');
        const res = await fetch(`${this.API_URL}/auth/resend-verification`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to resend verification');
        return data;
    }

    async requestPasswordReset(email) {
        const res = await fetch(`${this.API_URL}/auth/forgot-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to request password reset');
        return data;
    }

    async resetPassword(token, newPassword) {
        const res = await fetch(`${this.API_URL}/auth/reset-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, newPassword })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to reset password');

        // Force logout to ensure clean state and prevent confusion with old sessions
        this.logout();

        return data;
    }

    async getAdminSettings() {
        if (!this.token) return null;
        const res = await fetch(`${this.API_URL}/admin/settings`, {
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch admin settings');
        return await res.json();
    }

    async saveAdminSettings(settings) {
        if (!this.token) return null;
        const res = await fetch(`${this.API_URL}/admin/settings`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.token}`
            },
            body: JSON.stringify({ settings })
        });
        if (!res.ok) {
            const data = await res.json();
            throw new Error(data.error || 'Failed to save admin settings');
        }
        return await res.json();
    }

    async testSmtp(settings) {
        if (!this.token) return null;
        const res = await fetch(`${this.API_URL}/admin/test-smtp`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.token}`
            },
            body: JSON.stringify({ settings })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'SMTP test failed');
        return data;
    }

    async changeAdminPassword(newPassword) {
        if (!this.token) return null;
        const res = await fetch(`${this.API_URL}/admin/change-password`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.token}`
            },
            body: JSON.stringify({ newPassword })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to change password');
        return data;
    }

    async getAdminUsers() {
        if (!this.token) return [];
        const res = await fetch(`${this.API_URL}/admin/users`, {
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch users');
        return await res.json();
    }

    async getAdminUserDetails(userId) {
        if (!this.token) return null;
        const res = await fetch(`${this.API_URL}/admin/users/${userId}`, {
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch user details');
        return await res.json();
    }

    async updateAdminUser(userId, userData) {
        if (!this.token) return null;
        const res = await fetch(`${this.API_URL}/admin/users/${userId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${this.token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(userData)
        });
        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.error || 'Failed to update user');
        }
        return await res.json();
    }

    async getAdminUserFeedback(userId) {
        if (!this.token) return [];
        const res = await fetch(`${this.API_URL}/admin/users/${userId}/feedback`, {
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch user feedback');
        return await res.json();
    }

    async deleteAdminUser(userId) {
        if (!this.token) return null;
        const res = await fetch(`${this.API_URL}/admin/users/${userId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.error || 'Failed to delete user');
        }
        return await res.json();
    }

    /**
     * Fetches non-admin and non-test user recipients for notification emailing.
     */
    async getAdminEmailRecipients() {
        if (!this.token) return [];
        const res = await fetch(`${this.API_URL}/admin/email/recipients`, {
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.error || 'Failed to fetch email recipients');
        }
        return await res.json();
    }

    /**
     * Sends notification email from admin.
     * @param {Object} emailData
     */
    async sendAdminEmail(emailData) {
        if (!this.token) return null;
        const res = await fetch(`${this.API_URL}/admin/email/send`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${this.token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(emailData)
        });
        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.error || 'Failed to send email');
        }
        return await res.json();
    }

    /**
     * Secret backdoor for UI testing. Bypasses backend auth.
     */
    mockAuthenticate() {
        this.currentUser = {
            id: -1,
            email: 'test@mock.com',
            name: 'Test Admin',
            tier: 'pro',
            max_licenses: 1000,
            licensedTranscriptions: []
        };
        this.token = 'mock-test-token';
        this.saveToStorage();
        if (this.onChanged) this.onChanged(this.currentUser);
    }

    async changePassword(currentPassword, newPassword) {
        if (!this.token) return;

        const res = await fetch(`${this.API_URL}/user/change-password`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${this.token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ currentPassword, newPassword })
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to change password');
        return data;
    }

    async deleteAccount(reason) {
        if (!this.token) return;

        const res = await fetch(`${this.API_URL}/user/account`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${this.token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ reason })
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to delete account');
        return data;
    }

    logout() {
        if (this.token) {
            // Async logout notification to server
            fetch(`${this.API_URL}/auth/logout`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${this.token}` }
            }).catch(err => { /* silence logout logging failures */ });
        }

        if (window.mixpanel) {
            window.mixpanel.track('Logout');
            window.mixpanel.reset();
        }

        featureFlags.clear();
        this.currentUser = null;
        this.token = null;
        this.saveToStorage();
        if (this.onChanged) this.onChanged(null);
    }

    silentLogout() {
        this.currentUser = null;
        this.token = null;
        this.saveToStorage();
        // Do NOT trigger onChanged to avoid UI resets
    }

    isAuthenticated() {
        return !!this.currentUser && !!this.token;
    }

    isAdmin() {
        return this.currentUser?.is_admin === 1;
    }

    isVerified() {
        // Social users or verified email users
        return this.currentUser?.provider !== 'email' || this.currentUser?.is_verified === 1;
    }

    checkForFilenameCollision(filename, hash) {
        if (!this.currentUser || !this.currentUser.licensedTranscriptions) return false;
        // Check if any OTHER file (different hash) has the same name
        return this.currentUser.licensedTranscriptions.some(t => t.filename === filename && t.file_hash !== hash);
    }

    getTranscriptionStatus(hash) {
        if (featureFlags.isEnabled('free_mode')) {
            return 'licensed';
        }
        if (!this.currentUser) return 'limit_reached';

        const isLicensed = this.currentUser.licensedTranscriptions?.some(t => t.file_hash === hash);
        if (isLicensed) return 'licensed';

        // Check credits
        return (this.currentUser.license_credits > 0) ? 'available' : 'limit_reached';
    }

    async addLicensedTranscription(hash, filename) {
        const status = this.getTranscriptionStatus(hash);
        if (status === 'licensed') return true;

        if (featureFlags.isEnabled('free_mode')) {
            if (this.currentUser && this.currentUser.licensedTranscriptions) {
                this.currentUser.licensedTranscriptions.push({ file_hash: hash, filename });
            }
            // Asynchronously register license on backend for user or anonymous browser tracking
            try {
                await fetch(`${this.API_URL}/user/license`, {
                    method: 'POST',
                    headers: this.getHeaders({ 'Content-Type': 'application/json' }),
                    body: JSON.stringify({ fileHash: hash, filename })
                });
            } catch (e) {
                // Ignore network errors in free mode fallback
            }
            return true;
        }

        if (!this.currentUser && !this.getAnonymousId()) return false;

        // If we're mocking, don't hit the real server
        if (this.token === 'mock-test-token') {
            if (this.currentUser && this.currentUser.licensedTranscriptions) {
                this.currentUser.licensedTranscriptions.push({ file_hash: hash, filename });
            }
            if (this.currentUser) {
                this.currentUser.license_credits = Math.max(0, this.currentUser.license_credits - 1);
            }
            this.saveToStorage();
            if (this.onChanged && this.currentUser) this.onChanged(this.currentUser);
            return true;
        }

        const res = await fetch(`${this.API_URL}/user/license`, {
            method: 'POST',
            headers: this.getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ fileHash: hash, filename })
        });

        const result = await res.json();
        if (result.success && this.currentUser) {
            await this.refreshProfile();
        }
        return result;
    }

    async getUserState() {
        if (!this.token && !this.getAnonymousId()) return {};
        try {
            const res = await fetch(`${this.API_URL}/user/state`, {
                headers: this.getHeaders()
            });
            if (res.ok) return await res.json();
        } catch (err) {
            // console.error('Failed to get user state:', err);
        }
        return {};
    }

    async saveUserState(state) {
        if (!this.token && !this.getAnonymousId()) return;
        try {
            await fetch(`${this.API_URL}/user/state`, {
                method: 'POST',
                headers: this.getHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ state })
            });
        } catch (err) {
            // console.error('Failed to save user state:', err);
        }
    }

    async deleteUser(userId) {
        if (!this.token) throw new Error('Not authenticated');
        const res = await fetch(`${this.API_URL}/admin/users/${userId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        const result = await res.json();
        if (!res.ok) throw new Error(result.error);
        return result;
    }

    getLicenseCounts() {
        if (!this.currentUser) return { credits: 0 };
        return {
            credits: this.currentUser.license_credits || 0
        };
    }

    async deleteUserLicenses(userId, licenseIds) {
        if (!this.token) throw new Error('Not authenticated');

        const response = await fetch(`${this.API_URL}/admin/users/${userId}/licenses`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${this.token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ licenseIds })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to delete licenses');
        }

        return await response.json();
    }

    /**
     * Submits user feedback via the backend API.
     *
     * @param {string} subject - The feedback subject line.
     * @param {string} body - The feedback body text.
     * @returns {Promise<Object>} Server response.
     */
    async submitFeedback(subject, body) {
        if (!this.token) return;
        const res = await fetch(`${this.API_URL}/user/feedback`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.token}`
            },
            body: JSON.stringify({ subject, body })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to submit feedback');
        return data;
    }

    // --- Feature Flag Admin Methods ---

    /**
     * Returns all feature flag definitions (admin only).
     *
     * @returns {Promise<Array>} List of flag objects.
     */
    async getAdminFeatureFlags() {
        if (!this.token) return [];
        const res = await fetch(`${this.API_URL}/admin/feature-flags`, {
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch feature flags');
        return await res.json();
    }

    /**
     * Creates or updates a feature flag definition (admin only).
     * The `key` field is immutable after creation.
     *
     * @param {{key: string, name: string, description: string, is_enabled_default: boolean}} flag
     * @returns {Promise<Object>}
     */
    async saveAdminFeatureFlag(flag) {
        if (!this.token) return;
        const res = await fetch(`${this.API_URL}/admin/feature-flags`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.token}`
            },
            body: JSON.stringify(flag)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to save feature flag');
        return data;
    }

    /**
     * Deletes a feature flag and all its per-user overrides (admin only).
     *
     * @param {string} key - The snake_case key of the flag to delete.
     * @returns {Promise<Object>}
     */
    async deleteAdminFeatureFlag(key) {
        if (!this.token) return;
        const res = await fetch(`${this.API_URL}/admin/feature-flags/${encodeURIComponent(key)}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to delete feature flag');
        return data;
    }

    /**
     * Returns all feature flag states (with override info) for a given user (admin only).
     *
     * @param {number|string} userId
     * @returns {Promise<Array>}
     */
    async getUserFeatureFlagOverrides(userId) {
        if (!this.token) return [];
        const res = await fetch(`${this.API_URL}/admin/users/${userId}/feature-flags`, {
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch user feature flag overrides');
        return await res.json();
    }

    /**
     * Sets a per-user feature flag override (admin only).
     *
     * @param {number|string} userId
     * @param {string} flagKey - The snake_case flag key.
     * @param {boolean} isEnabled
     * @returns {Promise<Object>}
     */
    async setUserFeatureFlagOverride(userId, flagKey, isEnabled) {
        if (!this.token) return;
        const res = await fetch(`${this.API_URL}/admin/users/${userId}/feature-flags`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.token}`
            },
            body: JSON.stringify({ flagKey, isEnabled })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to set feature flag override');
        return data;
    }

    /**
     * Removes a per-user feature flag override, reverting to the global default (admin only).
     *
     * @param {number|string} userId
     * @param {string} flagKey - The snake_case flag key.
     * @returns {Promise<Object>}
     */
    async removeUserFeatureFlagOverride(userId, flagKey) {
        if (!this.token) return;
        const res = await fetch(
            `${this.API_URL}/admin/users/${userId}/feature-flags/${encodeURIComponent(flagKey)}`,
            {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${this.token}` }
            }
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to remove feature flag override');
        return data;
    }

    /**
     * Force-logs out a user by deleting all their server-side sessions (admin
     * only). The user's JWT will be rejected on their next API call, causing
     * the client to clear localStorage and redirect to the login screen.
     *
     * @param {number|string} userId
     * @returns {Promise<Object>}
     */
    async forceLogoutUser(userId) {
        if (!this.token) throw new Error('Not authenticated');
        const res = await fetch(`${this.API_URL}/admin/users/${userId}/force-logout`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to force logout user');
        return data;
    }
}

export const accountManager = new AccountManager();

